// ─────────────────────────────────────────────────────────────────────────────
// Validation — mirrors AskQuestionValidator from the Application layer.
// Inlined here so the compiled output is a single JS file with no imports,
// which works reliably in the browser without a bundler or type="module".
// ─────────────────────────────────────────────────────────────────────────────

interface PackageEntry {
    name: string;
    version: string;
}

interface ValidationResult {
    valid: boolean;
    errors: Record<string, string>;
}

let parentMessageDetailsGuid: string | undefined;

function validateDotNetVersion(value: string): string {
    if (!value || value.trim() === '') return 'A .NET version is required.';

    switch (value) {
        case "6":
        case "7":
        case "8":
        case "9":
        case "10":
            return '';
        default:
            return 'DotNetVersion must be a numeric value (e.g. 8, 9, 10).'        
    }
}

function validateQuestion(value: string): string {
    const v = value.trim();
    if (!v) return 'A question is required.';
    if (v.length < 10) return 'Your question is too short — please provide more detail.';
    if (v.length > 2000) return 'Question must be 2000 characters or fewer.';
    return '';
}

function validatePackageName(name: string): string {
    if (!name || name.trim() === '') return 'Package name cannot be empty.';
    if (name.trim().length < 2) return 'Package name must be at least 2 characters.';
    if (name.trim().length > 100) return 'Package name must be 100 characters or fewer.';
    return '';
}

function validatePackageVersion(pkg: PackageEntry): string {
    if (!pkg.version || pkg.version.trim() === '') return '';
    if (pkg.version.trim().length > 30) return 'Package version must be 30 characters or fewer.';

    return '';
}

function validateAll(
    dotNetVersion: string,
    question: string,
    packages: PackageEntry[]
): ValidationResult {
    const errors: Record<string, string> = {};

    const dotNetErr = validateDotNetVersion(dotNetVersion);
    if (dotNetErr) errors['dotNetVersion'] = dotNetErr;

    const questionErr = validateQuestion(question);
    if (questionErr) errors['question'] = questionErr;

    packages.forEach((pkg, i) => {
        const nameErr = validatePackageName(pkg.name);
        if (nameErr) errors[`packages[${i}].name`] = nameErr;

        const vErr = validatePackageVersion(pkg);
        if (vErr) errors[`packages[${i}].version`] = vErr;
    });

    return { valid: Object.keys(errors).length === 0, errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const API_ENDPOINT  = '/api/message-details/send';

const SUGGESTED_PACKAGES: string[] = [
    'Entity Framework Core',
    'FluentValidation',
    'MediatR',
    'Serilog',
    'AutoMapper',
    'Dapper',
    'Polly',
    'Carter',
    'FastEndpoints',
    'Mapster',
    'Scrutor',
    'xUnit',
    'Bogus',
    'NSubstitute',
    'Refit',
];

// ─────────────────────────────────────────────────────────────────────────────
// Syntax highlighter
// Strategy: highlight tokens first on raw code, then escape HTML only in the
// text nodes between spans. This ensures <span> tags are real HTML while
// code characters like < > & inside strings/comments are safely escaped.
// ─────────────────────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function highlight(code: string): string {
    // Split into lines and process each line so comments don't bleed across lines
    return code.split('\n').map(line => {
        // 1. Pull out any // comment at the end of the line (before HTML escaping)
        const commentIdx = line.indexOf('//');
        const codePart   = commentIdx >= 0 ? line.slice(0, commentIdx) : line;
        const comment    = commentIdx >= 0 ? line.slice(commentIdx)     : '';

        // 2. Tokenise the code part: strings vs plain tokens
        const tokens: string[] = [];
        const strRe = /"(?:[^"\\]|\\.)*"/g;
        let last = 0;
        let m: RegExpExecArray | null;

        while ((m = strRe.exec(codePart)) !== null) {
            // plain text before this string
            if (m.index > last) tokens.push(escapeHtml(codePart.slice(last, m.index)));
            // the string literal itself
            tokens.push(`<span class="hl-str">${escapeHtml(m[0])}</span>`);
            last = m.index + m[0].length;
        }
        // remainder after last string
        if (last < codePart.length) tokens.push(escapeHtml(codePart.slice(last)));

        // 3. Apply keyword highlighting only to plain-text segments (not inside spans)
        const kwRe = /\b(public|private|internal|protected|static|async|await|new|return|var|using|namespace|class|record|struct|interface|void|string|int|bool|Task|IResult|TypedResults|ValueTask|required|init|sealed|override|virtual|abstract|readonly|const|null|true|false)\b/g;
        const highlighted = tokens.map(tok =>
            tok.startsWith('<span') ? tok : tok.replace(kwRe, '<span class="hl-kw">$1</span>')
        ).join('');

        // 4. Append comment (escaped) if present
        const commentHtml = comment
            ? `<span class="hl-cmt">${escapeHtml(comment)}</span>`
            : '';

        return highlighted + commentHtml;
    }).join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Prose renderer — handles inline backtick code spans
// ─────────────────────────────────────────────────────────────────────────────

function renderProse(text: string, container: HTMLElement): void {
    const p = document.createElement('p');
    p.className = 'answer-text';

    // Split on `inline code` spans
    const parts = text.split(/(`[^`]+`)/g);
    parts.forEach(part => {
        if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
            const code = document.createElement('code');
            code.className = 'inline-code';
            code.textContent = part.slice(1, -1);
            p.appendChild(code);
        } else if (part) {
            p.appendChild(document.createTextNode(part));
        }
    });

    container.appendChild(p);
}

// ─────────────────────────────────────────────────────────────────────────────
// Streaming — state machine with three modes:
//   'prose'  — streaming characters into a <p>, watching for opening ```
//   'fence'  — consuming the opening ```lang\n fence line
//   'code'   — streaming characters directly into a live <pre>, watching for ```
// Code is highlighted line-by-line as each \n arrives so it's styled live.
// ─────────────────────────────────────────────────────────────────────────────

function streamText(
    target: HTMLElement,
    fullText: string,
    onComplete: (text: string) => void
): void {
    let i = 0;
    type Mode = 'prose' | 'fence' | 'code';
    let mode: Mode = 'prose';

    // Prose state
    let proseBuffer  = '';
    let activeProse: HTMLParagraphElement | null = null;

    // Code state
    let fenceBuffer  = '';   // accumulates the ```lang\n opening
    let codeRaw      = '';   // raw unescaped code accumulated so far
    let activeBlock: HTMLDivElement | null = null;
    let activePre:   HTMLPreElement | null = null;
    const cursor = document.createElement('span');
    cursor.className = 'stream-cursor';
    target.appendChild(cursor);

    // Render the accumulated codeRaw into activePre with syntax highlighting
    function repaintPre(): void {
        if (activePre) activePre.innerHTML = highlight(codeRaw);
    }

    function commitProse(): void {
        if (!proseBuffer.trim()) { proseBuffer = ''; return; }
        if (activeProse) {
            activeProse.innerHTML = '';
            const parts = proseBuffer.trim().split(/(`[^`]+`)/g);
            parts.forEach(part => {
                if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
                    const c = document.createElement('code');
                    c.className = 'inline-code';
                    c.textContent = part.slice(1, -1);
                    activeProse!.appendChild(c);
                } else if (part) {
                    activeProse!.appendChild(document.createTextNode(part));
                }
            });
        }
        proseBuffer = '';
        activeProse = null;
    }

    function makeCopyButton(): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.className = 'answer-code__copy';
        btn.textContent = 'Copy';
        return btn;
    }

    function wireCopyButton(btn: HTMLButtonElement, code: string): void {
        btn.onclick = () => {
            const succeed = () => {
                btn.textContent = '✓ Copied';
                btn.classList.add('is-copied');
                setTimeout(() => {
                    btn.textContent = 'Copy';
                    btn.classList.remove('is-copied');
                }, 2000);
            };
            if (navigator.clipboard) {
                navigator.clipboard.writeText(code).then(succeed).catch(() => fallbackCopy(code, succeed));
            } else {
                fallbackCopy(code, succeed);
            }
        };
    }

    function fallbackCopy(text: string, onSuccess: () => void): void {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try { document.execCommand('copy'); onSuccess(); } catch {}
        document.body.removeChild(ta);
    }

    function openCodeBlock(): void {
        activeBlock = document.createElement('div');
        activeBlock.className = 'answer-code';

        // Copy button is wired with the final code in closeCodeBlock
        const copyBtn = makeCopyButton();

        activePre = document.createElement('pre');
        activeBlock.appendChild(copyBtn);
        activeBlock.appendChild(activePre);
        target.insertBefore(activeBlock, cursor);
    }

    function closeCodeBlock(): void {
        const frozenCode = codeRaw;          // capture before clearing
        repaintPre();

        // Wire the copy button now that we have the complete code
        const copyBtn = activeBlock!.querySelector<HTMLButtonElement>('.answer-code__copy')!;
        wireCopyButton(copyBtn, frozenCode);

        activeBlock = null;
        activePre   = null;
        codeRaw     = '';
        fenceBuffer = '';
    }

    const interval = setInterval(() => {
        if (i >= fullText.length) {
            clearInterval(interval);
            cursor.remove();
            if (mode === 'prose' && proseBuffer.trim()) commitProse();
            onComplete(fullText);
            return;
        }

        const ch = fullText[i++];

        if (mode === 'prose') {
            proseBuffer += ch;

            // Detect opening ```
            if (proseBuffer.endsWith('```')) {
                // Commit prose text up to the ```
                proseBuffer = proseBuffer.slice(0, -3);
                commitProse();
                fenceBuffer = '```';
                mode = 'fence';
            } else {
                // Update live prose paragraph
                if (!activeProse) {
                    activeProse = document.createElement('p');
                    activeProse.className = 'answer-text';
                    target.insertBefore(activeProse, cursor);
                }
                // Show plain text while streaming (inline code rendered on commit)
                activeProse.textContent = proseBuffer.trim();
            }

        } else if (mode === 'fence') {
            // Consume "```csharp\n" — wait for the newline that ends the fence
            fenceBuffer += ch;
            if (ch === '\n') {
                openCodeBlock();
                mode = 'code';
            }

        } else { // mode === 'code'
            if (ch === '`' && fullText.slice(i - 1, i + 2) === '```') {
                // Consume the full closing ```
                i += 2;
                closeCodeBlock();
                mode = 'prose';
            } else {
                codeRaw += ch;
                // Repaint on every newline so each completed line is highlighted live
                if (ch === '\n') repaintPre();
            }
        }

        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 8);
}

// ─────────────────────────────────────────────────────────────────────────────
// UI helpers
// ─────────────────────────────────────────────────────────────────────────────

function setError(id: string, msg: string): void {
    const el = document.getElementById(id);
    if (el) el.textContent = msg;
}

function clearErrors(): void {
    document.querySelectorAll<HTMLElement>('.field__error').forEach(el => el.textContent = '');
    document.querySelectorAll<HTMLElement>('.pkg-chip__version-error, .pkg-chip__name-error').forEach(el => {
        el.textContent = '';
        el.classList.remove('is-visible');
    });
    document.querySelectorAll<HTMLElement>('.pkg-chip__version-input').forEach(el => {
        el.classList.remove('is-invalid');
    });
    document.querySelectorAll<HTMLElement>('.pkg-chip').forEach(el => {
        el.classList.remove('is-invalid');
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Package management
// ─────────────────────────────────────────────────────────────────────────────

const packages: PackageEntry[] = [];

function renderPackageChip(entry: PackageEntry, index: number): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'pkg-chip';
    li.dataset.index = String(index);

    // ── Top row: name | version input | remove button ──
    const row = document.createElement('div');
    row.className = 'pkg-chip__row';

    const nameSpan = document.createElement('span');
    nameSpan.className = 'pkg-chip__name';
    nameSpan.textContent = entry.name;

    const versionInput = document.createElement('input');
    versionInput.className = 'pkg-chip__version-input';
    versionInput.type = 'text';
    versionInput.placeholder = 'version e.g. 9.0.0';
    versionInput.value = entry.version;
    versionInput.setAttribute('aria-label', `Version for ${entry.name}`);

    const removeBtn = document.createElement('button');
    removeBtn.className = 'pkg-chip__remove';
    removeBtn.type = 'button';
    removeBtn.textContent = '✕';
    removeBtn.setAttribute('aria-label', `Remove ${entry.name}`);
    removeBtn.addEventListener('click', () => {
        packages.splice(index, 1);
        renderAllChips();
    });

    row.appendChild(nameSpan);
    row.appendChild(versionInput);
    row.appendChild(removeBtn);

    // ── Error messages ──
    const errorsWrap = document.createElement('div');
    errorsWrap.className = 'pkg-chip__errors';

    const nameError = document.createElement('span');
    nameError.className = 'pkg-chip__name-error';
    nameError.setAttribute('role', 'alert');

    const versionError = document.createElement('span');
    versionError.className = 'pkg-chip__version-error';
    versionError.setAttribute('role', 'alert');

    errorsWrap.appendChild(nameError);
    errorsWrap.appendChild(versionError);

    // ── Live version validation on input ──
    versionInput.addEventListener('input', () => {
        entry.version = versionInput.value;
        const err = validatePackageVersion(entry);
        if (err) {
            versionInput.classList.add('is-invalid');
            versionError.textContent = err;
            versionError.classList.add('is-visible');
        } else {
            versionInput.classList.remove('is-invalid');
            versionError.textContent = '';
            versionError.classList.remove('is-visible');
        }
    });

    li.appendChild(row);
    li.appendChild(errorsWrap);

    return li;
}

function renderAllChips(): void {
    const chipsEl = document.getElementById('pkg-chips');
    if (!chipsEl) return;
    chipsEl.innerHTML = '';
    packages.forEach((pkg, i) => {
        chipsEl.appendChild(renderPackageChip(pkg, i));
    });
}

function initPackageSearch(): void {
    const input       = document.getElementById('pkg-search') as HTMLInputElement;
    const suggestions = document.getElementById('pkg-suggestions') as HTMLUListElement;
    if (!input || !suggestions) return;

    function showSuggestions(query: string): void {
        suggestions.innerHTML = '';

        const filtered = SUGGESTED_PACKAGES.filter(
            s => s.toLowerCase().includes(query.toLowerCase()) &&
                 !packages.find(p => p.name === s)
        );

        if (filtered.length === 0 && !query.trim()) {
            suggestions.setAttribute('hidden', '');
            input.setAttribute('aria-expanded', 'false');
            return;
        }

        filtered.forEach(name => {
            const li = document.createElement('li');
            li.textContent = name;
            li.setAttribute('role', 'option');
            li.addEventListener('mousedown', (e) => {
                e.preventDefault();
                addPackage(name);
            });
            suggestions.appendChild(li);
        });

        const trimmed = query.trim();
        if (trimmed && !SUGGESTED_PACKAGES.some(s => s.toLowerCase() === trimmed.toLowerCase())) {
            const li = document.createElement('li');
            li.textContent = `+ Add "${trimmed}"`;
            li.className = 'pkg-suggestion--custom';
            li.setAttribute('role', 'option');
            li.addEventListener('mousedown', (e) => {
                e.preventDefault();
                addPackage(trimmed);
            });
            suggestions.appendChild(li);
        }

        if (suggestions.children.length > 0) {
            suggestions.removeAttribute('hidden');
            input.setAttribute('aria-expanded', 'true');
        } else {
            suggestions.setAttribute('hidden', '');
            input.setAttribute('aria-expanded', 'false');
        }
    }

    function addPackage(name: string): void {
        if (packages.find(p => p.name === name)) return;
        packages.push({ name, version: '' });
        renderAllChips();
        input.value = '';
        suggestions.setAttribute('hidden', '');
        input.setAttribute('aria-expanded', 'false');
        input.focus();
    }

    input.addEventListener('input', () => showSuggestions(input.value));
    input.addEventListener('focus', () => showSuggestions(input.value));
    input.addEventListener('blur', () => {
        setTimeout(() => {
            suggestions.setAttribute('hidden', '');
            input.setAttribute('aria-expanded', 'false');
        }, 150);
    });
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const first = suggestions.querySelector('li');
            if (first) (first as HTMLLIElement).dispatchEvent(new Event('mousedown'));
        }
        if (e.key === 'Escape') {
            suggestions.setAttribute('hidden', '');
            input.setAttribute('aria-expanded', 'false');
        }
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// API call
// ─────────────────────────────────────────────────────────────────────────────

interface MessageDetailsRequest {
    parentMessageDetailsGuid?: string;
    dotNetVersion: string;
    packages: PackageEntry[];
    question: string;
    cookieId: string;
}

interface MessageDetailsResponse {
    guid: string;
    response: string;
    created: string;
}

async function callApi(req: MessageDetailsRequest): Promise<MessageDetailsResponse> {
    const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => ({}));

        let detail = 'I am currently unable to process your request. Please try again later.';
        if (res.status == 400 && body?.title) {
            detail = body.title;
        }

        throw new Error(detail);
    }

    return res.json() as Promise<MessageDetailsResponse>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversation builder
// ─────────────────────────────────────────────────────────────────────────────

function appendExchange(questionText: string): {
    answerBody: HTMLElement;
    onAnswerComplete: (text: string) => void;
} {
    const conversation = document.getElementById('conversation')!;

    const exchange = document.createElement('div');
    exchange.className = 'exchange';

    // Question bubble
    const qWrap = document.createElement('div');
    qWrap.className = 'exchange__question';

    const qLabel = document.createElement('span');
    qLabel.className = 'exchange__question-label';
    qLabel.setAttribute('aria-hidden', 'true');
    qLabel.textContent = 'You';

    const qBubble = document.createElement('div');
    qBubble.className = 'exchange__question-bubble';
    qBubble.textContent = questionText;

    qWrap.appendChild(qLabel);
    qWrap.appendChild(qBubble);

    // Answer area
    const aWrap = document.createElement('div');
    aWrap.className = 'exchange__answer';

    const aHeader = document.createElement('div');
    aHeader.className = 'exchange__answer-header';

    const dot = document.createElement('span');
    dot.className = 'exchange__answer-dot';

    const aLabel = document.createElement('span');
    aLabel.className = 'exchange__answer-label';
    aLabel.textContent = 'Response';

    const aMeta = document.createElement('span');
    aMeta.className = 'exchange__answer-meta';
    aMeta.id = `meta-${Date.now()}`;

    aHeader.appendChild(dot);
    aHeader.appendChild(aLabel);
    aHeader.appendChild(aMeta);

    const answerBody = document.createElement('div');
    answerBody.className = 'exchange__answer-body';

    aWrap.appendChild(aHeader);
    aWrap.appendChild(answerBody);

    exchange.appendChild(qWrap);
    exchange.appendChild(aWrap);
    conversation.appendChild(exchange);

    exchange.scrollIntoView({ behavior: 'smooth', block: 'start' });

    function onAnswerComplete(text: string): void {
        const version = (document.getElementById('dotnet-version') as HTMLSelectElement)?.value;
        //if (version) aMeta.textContent = `.NET ${version}`;

        const followup = buildFollowupForm(exchange);
        exchange.appendChild(followup);
    }

    return { answerBody, onAnswerComplete };
}

function buildFollowupForm(parentExchange: HTMLElement): HTMLElement {
    const form = document.createElement('div');
    form.className = 'followup-form';

    const input = document.createElement('input');
    input.className = 'followup-form__input';
    input.type = 'text';
    input.placeholder = 'Ask a follow-up…';
    input.maxLength = 2000;
    input.setAttribute('aria-label', 'Follow-up question');

    const btn = document.createElement('button');
    btn.className = 'followup-form__btn';
    btn.type = 'button';
    btn.textContent = 'Ask →';

    const submit = async (): Promise<void> => {
        const q = input.value.trim();
        const qErr = validateQuestion(q);
        if (qErr) {
            input.classList.add('is-invalid');
            input.setAttribute('aria-invalid', 'true');
            return;
        }
        input.classList.remove('is-invalid');
        input.setAttribute('aria-invalid', 'false');

        btn.disabled = true;
        input.disabled = true;
        form.remove(); // remove from parent, new exchange takes over

        await submitQuestion(q);
    };

    btn.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });

    form.appendChild(input);
    form.appendChild(btn);
    return form;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core submit handler
// ─────────────────────────────────────────────────────────────────────────────

async function submitQuestion(questionText: string): Promise<void> {
    const dotNetVersion = document.getElementById('dotnet-version') as HTMLSelectElement
    const packageName = document.getElementById('pkg-search') as HTMLInputElement;
    const versions =  document.querySelectorAll<HTMLInputElement>('.pkg-chip__version-input');
    const removeButtons = document.querySelectorAll<HTMLButtonElement>('.pkg-chip__remove');
    const textarea  = document.getElementById('question') as HTMLTextAreaElement;
    const submitBtn     = document.getElementById('btn-ask') as HTMLButtonElement;

    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('btn-ask--loading');
    }

    const { answerBody, onAnswerComplete } = appendExchange(questionText);

    const spinner = document.createElement('div');
    spinner.className = 'answer-text';
    spinner.textContent = 'Thinking…';
    answerBody.appendChild(spinner);

    let cookieId = getCookie('message-details-id');

    if (!cookieId) {
        // Create a cookie if it's not set.
        cookieId = crypto.randomUUID();
    }
    // Update the cookie to reset in 30 days.
    setCookie('message-details-id', cookieId, 30);

    try {
        const response = await callApi({
            parentMessageDetailsGuid,
            dotNetVersion: dotNetVersion?.value ?? '',
            packages: [...packages],
            question: questionText,
            cookieId
        });

        if (!parentMessageDetailsGuid) {
            parentMessageDetailsGuid = response.guid;
        }

        spinner.remove();

        streamText(answerBody, response.response, () => {
            onAnswerComplete(response.response);

            if (submitBtn) {
                submitBtn.classList.remove('btn-ask--loading');
                
                dotNetVersion.disabled = true;
                packageName.disabled = true;
                versions.forEach(v => {
                    v.disabled = true;
                });
                removeButtons.forEach(v => {
                    v.disabled = true;
                });
                textarea.disabled = true;
            }
        });

    } catch (err: unknown) {
        spinner.remove();
        const msg = err instanceof Error ? err.message : 'Unexpected error. Please try again.';
        const errEl = document.createElement('p');
        errEl.className = 'answer-text';
        errEl.style.color = '#f87171';
        errEl.textContent = `⚠ ${msg}`;
        answerBody.appendChild(errEl);

        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('btn-ask--loading');
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Form submit — validates then calls submitQuestion
// ─────────────────────────────────────────────────────────────────────────────

function initForm(): void {
    const askBtn    = document.getElementById('btn-ask') as HTMLButtonElement;
    const textarea  = document.getElementById('question') as HTMLTextAreaElement;
    const charCount = document.getElementById('char-count');

    if (!askBtn) return;

    // Character counter
    textarea?.addEventListener('input', () => {
        const len = textarea.value.length;
        if (charCount) charCount.textContent = `${len} / 2000`;
    });

    // Inline validation on blur/change
    (document.getElementById('dotnet-version') as HTMLSelectElement)
        ?.addEventListener('change', () => {
            const err = validateDotNetVersion(
                (document.getElementById('dotnet-version') as HTMLSelectElement).value
            );
            setError('error-dotnet', err);
        });

    textarea?.addEventListener('blur', () => {
        setError('error-question', validateQuestion(textarea.value));
    });

    // Ctrl/Cmd+Enter shortcut
    textarea?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAsk();
    });

    askBtn.addEventListener('click', handleAsk);
}

async function handleAsk(): Promise<void> {
    clearErrors();

    const dotNetVersion = (document.getElementById('dotnet-version') as HTMLSelectElement)?.value ?? '';
    const textarea      = document.getElementById('question') as HTMLTextAreaElement;
    const charCount     = document.getElementById('char-count');
    const questionText  = textarea?.value ?? '';

    const { valid, errors } = validateAll(dotNetVersion, questionText, packages);

    if (!valid) {
        if (errors['dotNetVersion']) setError('error-dotnet', errors['dotNetVersion']);
        if (errors['question'])      setError('error-question', errors['question']);

        // Per-package errors (name + version)
        packages.forEach((_, i) => {
            const chip = document.querySelector<HTMLElement>(`.pkg-chip[data-index="${i}"]`);
            if (!chip) return;

            const nameKey = `packages[${i}].name`;
            if (errors[nameKey]) {
                chip.classList.add('is-invalid');
                const nameErr = chip.querySelector<HTMLElement>('.pkg-chip__name-error');
                if (nameErr) { nameErr.textContent = errors[nameKey]; nameErr.classList.add('is-visible'); }
            }

            const versionKey = `packages[${i}].version`;
            if (errors[versionKey]) {
                const vInput = chip.querySelector<HTMLElement>('.pkg-chip__version-input');
                const vErr   = chip.querySelector<HTMLElement>('.pkg-chip__version-error');
                vInput?.classList.add('is-invalid');
                if (vErr) { vErr.textContent = errors[versionKey]; vErr.classList.add('is-visible'); }
            }
        });

        // Focus first error
        const container = document.getElementById('ask-form');
        const firstErr  = container?.querySelector<HTMLElement>('.is-invalid, [aria-invalid="true"]');
        firstErr?.focus();
        return;
    }

    if (textarea) textarea.value = '';
    if (charCount) charCount.textContent = '0 / 2000';

    await submitQuestion(questionText);
}

function setCookie(name: string, value: string, days: number = 365): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Strict`;
}

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Boot
// ─────────────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    initPackageSearch();
    renderAllChips();
    initForm();
});
